import { Button, FormControl, FormHelperText, FormLabel, Input, Stack } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import OrientationIcon from '@/assets/icons/orientation-arrow.svg?react';
import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { useBearingEstimation } from '@/hooks/useBearingEstimation';
import { I18N_DE } from '@/i18n/de';
import { CreateSignGroupDto, SignGroupInstallationSide, SignGroupType } from '@/modules/api';
import {
  backendApiSignGroupsCreateMutation,
  backendApiSignSupportsRemoveMutation,
} from '@/modules/api/@tanstack/react-query.gen';
import { useSignSupportQuery } from '@/queries/useSignSupportQuery';
import { useCreateSignStore } from '@/stores/useCreateSignStore';
import { CreateSignStep } from '@/types/CreateSignStep';

export const Route = createFileRoute(
  '/_authenticated/_management/verkehrszeichen/$adminLevelId/$signSupportId/hinzufuegen',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: CreateSignGroupRoute,
});

function CreateSignGroupRoute() {
  const formId = useId();
  const navigate = useNavigate();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: support } = useSignSupportQuery();

  const { adminLevelId, signSupportId } = Route.useParams();

  const step = useCreateSignStore((state) => state.step);
  const { setStep } = useCreateSignStore((state) => state.actions);
  const {
    control,
    watch,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<Pick<CreateSignGroupDto, 'type' | 'bearing' | 'installationSide' | 'roadsideDistance' | 'sibId'>>({
    defaultValues: { type: SignGroupType.NONE },
  });

  const currentBearing = watch('bearing');
  const bearingEstimations = useBearingEstimation();

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignGroupsCreateMutation(),
    onSuccess: ({ id }) => {
      if ([CreateSignStep.GROUP, CreateSignStep.ADD_GROUP].includes(step)) setStep(CreateSignStep.SIGN);
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId/hinzufuegen',
        params: {
          adminLevelId,
          signSupportId,
          signGroupId: id,
        },
      });
    },
  });

  const { mutateAsync: removeMutate, isPending: removeIsPending } = useMutation({
    ...backendApiSignSupportsRemoveMutation(),
    onSuccess: async () =>
      navigate({
        to: '/verkehrszeichen/$adminLevelId',
        params: { adminLevelId },
      }),
  });

  return (
    support && (
      <>
        <EditModalDialog
          title="Neue Verkehrszeichen-Gruppe hinzufügen"
          onClose={
            step === CreateSignStep.GROUP
              ? () => {
                  setConfirmOpen(true);
                }
              : undefined
          }
        >
          <Stack
            component="form"
            id={formId}
            onSubmit={handleSubmit(({ type, bearing, installationSide, roadsideDistance }) => {
              if (isPending) return;

              mutate({
                body: {
                  type,
                  supportId: support.id,
                  order: support.groups?.length || 0,
                  installationSide,
                  ...(bearing && { bearing }),
                  ...(roadsideDistance && {
                    roadsideDistance: roadsideDistance * 100,
                  }),
                },
              });
            })}
            gap={2}
          >
            <FormControl error={!!errors.type}>
              <FormLabel>Typ</FormLabel>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    orientation="horizontal"
                    options={Object.values(SignGroupType)}
                    optionLabel={({ option }) => I18N_DE.SignGroupType[option]}
                  />
                )}
              />
            </FormControl>

            <FormControl error={!!errors.installationSide}>
              <FormLabel>Aufstellungsseite</FormLabel>
              <Controller
                control={control}
                name="installationSide"
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    orientation="horizontal"
                    options={Object.values(SignGroupInstallationSide)}
                    optionLabel={({ option }) => I18N_DE.SignGroupInstallationSide[option]}
                  />
                )}
              />
            </FormControl>

            <FormControl error={!!errors.bearing}>
              <FormLabel>Abstand zum Fahrbahnrand</FormLabel>
              <Stack direction="row">
                <Input
                  type="number"
                  variant="outlined"
                  sx={{ width: 150 }}
                  slotProps={{
                    input: {
                      ...register('roadsideDistance'),
                      label: 'optional',
                      step: 0.1,
                    },
                  }}
                  endDecorator="Meter"
                />
              </Stack>
            </FormControl>

            <FormControl error={!!errors.sibId}>
              <FormLabel>Straßen-Kennung (SIB ID)</FormLabel>
              <Input
                type="text"
                variant="outlined"
                slots={{
                  input: FloatingInput,
                }}
                slotProps={{
                  input: {
                    ...register('sibId'),
                    label: 'optional',
                  },
                }}
              />
            </FormControl>

            <FormControl error={!!errors.bearing}>
              <FormLabel>Ausrichtung</FormLabel>
              <Stack direction="row" gap={1}>
                <Input
                  type="number"
                  variant="outlined"
                  sx={{ width: 150 }}
                  slotProps={{
                    input: {
                      ...register('bearing'),
                      label: 'optional',
                      min: 0,
                      max: 359,
                      step: 1,
                    },
                  }}
                  endDecorator="° Grad"
                />

                {currentBearing ? (
                  <OrientationIcon
                    style={{
                      transform: `rotate(${currentBearing}deg)`,
                      width: 30,
                      height: 30,
                      margin: 'auto 20px',
                    }}
                  />
                ) : (
                  bearingEstimations.map((estiamtion) => (
                    <Button key={estiamtion} variant="outlined" onClick={() => setValue('bearing', estiamtion)}>
                      <OrientationIcon
                        style={{
                          transform: `rotate(${estiamtion}deg)`,
                          width: 30,
                          height: 30,
                        }}
                      />
                    </Button>
                  ))
                )}
              </Stack>
            </FormControl>
            {error && <FormHelperText>{error.message}</FormHelperText>}

            <Button type="submit" form={formId} loading={isPending} disabled={isPending}>
              Speichern
            </Button>
          </Stack>
        </EditModalDialog>
        <ConfirmationModal
          title="Aufstellvorrichtung löschen"
          content="Sind Sie sicher?"
          open={confirmOpen}
          onAbort={() => setConfirmOpen(false)}
          onConfirm={async () => {
            setConfirmOpen(false);
            if (removeIsPending) return;

            await removeMutate({ path: { signSupportId } });
          }}
        />
      </>
    )
  );
}
